import { useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { addDays, isSameDay } from 'date-fns';
import { useBrainStore } from '@/state/brainStore';
import { useSimulationStore } from '@/state/simulationStore';
import { computePayoutForArtist } from '@/domain/commissionEngine';
import DashboardKPITile from '@/components/DashboardKPITile';
import { aud, simTimeFull, customerName } from '@/utils/format';
import { Sparkles, ScrollText, Footprints } from 'lucide-react';
import type { DayOfWeek } from '@/domain/types';

const DEAD_STATUSES = new Set(['cancelled', 'no-show']);
const DOW_LABEL: Record<DayOfWeek, string> = {
  mon: 'Mon', tue: 'Tue', wed: 'Wed', thu: 'Thu', fri: 'Fri', sat: 'Sat', sun: 'Sun',
};
const DOW_INDEX: DayOfWeek[] = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];

export default function ApprenticeDashboard() {
  const { apprenticeId } = useParams<{ apprenticeId: string }>();
  const navigate = useNavigate();
  const artists = useBrainStore((s) => s.artists);
  const customers = useBrainStore((s) => s.customers);
  const bookings = useBrainStore((s) => s.bookings);
  const deposits = useBrainStore((s) => s.deposits);
  const clockIso = useSimulationStore((s) => s.clockIso);
  const apprentice = apprenticeId ? artists[apprenticeId] : undefined;

  // The apprentice's own numbers, derived from the store (never hardcoded). Their
  // book is usually thin, so honest counts can be low - that is the real picture.
  const stats = useMemo(() => {
    if (!apprentice) return null;
    const now = new Date(clockIso);
    const weekEnd = addDays(now, 7);
    const mine = Object.values(bookings).filter((b) => b.artistId === apprentice.id);
    const live = mine.filter((b) => !DEAD_STATUSES.has(b.status));
    const today = live.filter((b) => isSameDay(new Date(b.start), now)).length;
    const thisWeek = live.filter((b) => {
      const t = new Date(b.start);
      return t >= now && t <= weekEnd;
    }).length;
    const ym = clockIso.slice(0, 7);
    const monthCompleted = mine.filter(
      (b) => b.status === 'completed' && (b.checkedOutAt ?? b.start).slice(0, 7) === ym,
    );
    const myDeposits = Object.values(deposits).filter((d) => d.artistId === apprentice.id);
    const depositsTotal = myDeposits
      .filter((d) => d.status === 'paid')
      .reduce((sum, d) => sum + (d.amountPaid ?? d.amountAud), 0);
    // Commission projection from this month's own completed sittings + their rates.
    const projection = computePayoutForArtist(
      apprentice,
      monthCompleted.map((b) => ({ bookingId: b.id, amountAud: b.dayRateAud ?? 0 })),
    ).payoutAud;
    // Rostered days this week, from their own pattern (drives the Front Desk line).
    const rosteredDays = DOW_INDEX.filter((d) => apprentice.availability.pattern[d]).map((d) => DOW_LABEL[d]);
    return { today, thisWeek, monthSessions: monthCompleted.length, depositsTotal, projection, rosteredDays };
  }, [apprentice, bookings, deposits, clockIso]);

  if (!apprentice || !stats) {
    return <p className="px-10 py-12 text-cream-soft italic">Unknown apprentice.</p>;
  }

  const ownInteractions = Object.values(customers).filter(
    (c) => c.primaryStudioId === apprentice.studioId,
  ).slice(0, 3);
  const rosterLine = stats.rosteredDays.length > 0 ? stats.rosteredDays.join(', ') : 'your rostered days';

  return (
    <section className="px-8 py-8" data-testid={`apprentice-dashboard-${apprentice.id}`}>
      <header className="flex items-end justify-between max-w-6xl">
        <div>
          <p className="label-small-caps text-gold-bright">Apprentice</p>
          <h1 className="heading-display text-3xl text-cream mt-2">{apprentice.displayName}</h1>
          <p className="font-inter text-xs text-cream-soft mt-2 uppercase tracking-widest">
            {apprentice.instagramHandle} · apprentice · {apprentice.studioId.replace('-', ' ')}
          </p>
        </div>
        <div className="grid grid-cols-3 gap-2">
          <DashboardKPITile label="Today" value={stats.today.toString()} />
          <DashboardKPITile label="This week" value={stats.thisWeek.toString()} />
          <DashboardKPITile label="Commission rate" value={`${(apprentice.commissionRate * 100).toFixed(0)}%`} />
        </div>
      </header>

      <div className="mt-6 grid grid-cols-12 gap-6 max-w-6xl">
        <section className="col-span-7">
          <h3 className="heading-serif text-base text-cream mb-3">Front Desk capture</h3>
          <div className="panel p-4">
            <p className="font-serif text-cream-soft text-sm">
              You are rostered {rosterLine} this week. Capture walk-ins in the Studio View
              Front Desk tab, then route to the bookings team for a follow-up.
            </p>
            <button
              type="button"
              onClick={() => navigate(`/studio/${apprentice.studioId}`)}
              className="btn-primary text-xs mt-4"
              data-testid="open-front-desk"
            >
              <Footprints size={12} /> Open Front Desk
            </button>
          </div>

          <h3 className="heading-serif text-base text-cream mt-6 mb-3">Your customers</h3>
          <ol className="space-y-2">
            {ownInteractions.length === 0 ? (
              <li className="font-serif text-cream-soft text-sm">
                No customers assigned yet. Studio Manager routes walk-ins your way.
              </li>
            ) : null}
            {ownInteractions.map((c) => (
              <li key={c.id} className="panel-dark p-3 flex items-center justify-between">
                <div>
                  <p className="font-inter text-sm text-cream">{customerName(c)}</p>
                  <p className="font-inter text-[10px] text-cream-soft uppercase tracking-widest mt-1">
                    {c.designIntent?.type ?? 'design intent pending'} · {simTimeFull(c.source.firstTouchAt)}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => navigate(`/customer/${c.id}`)}
                  className="btn-ghost text-xs"
                >
                  <Sparkles size={10} /> Open
                </button>
              </li>
            ))}
          </ol>
        </section>

        <aside className="col-span-5">
          <h3 className="heading-serif text-base text-cream mb-3">Earnings</h3>
          <div className="panel p-4 space-y-3">
            <Row label="Sessions this month" value={stats.monthSessions.toString()} />
            <Row label="Total deposits" value={aud(stats.depositsTotal)} />
            <Row label="Commission projection" value={aud(stats.projection)} accent />
          </div>

          <h3 className="heading-serif text-base text-cream mt-6 mb-3">Scope</h3>
          <div className="panel-dark p-4 text-xs space-y-2">
            <p className="text-cream">
              <ScrollText size={12} className="inline mr-2" /> Read-only on other artists&apos; bookings.
            </p>
            <p className="text-cream">
              <Sparkles size={12} className="inline mr-2" /> Cannot view network financials.
            </p>
            <p className="text-cream">
              <Footprints size={12} className="inline mr-2" /> Front-desk walk-in capture allowed.
            </p>
          </div>
        </aside>
      </div>
    </section>
  );
}

function Row({ label, value, accent }: { label: string; value: string; accent?: boolean }) {
  return (
    <div className="flex justify-between text-sm">
      <span className="text-cream-soft">{label}</span>
      <span className={accent ? 'font-inter text-gold-bright' : 'font-mono text-cream'}>
        {value}
      </span>
    </div>
  );
}
